import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm, SubmitHandler } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import Select, { MultiValue } from 'react-select'

import { Input, InputGroup } from '@components'
import Breadcrumb from '@components/Breadcrumb'
import Button from '@components/Button'
import Divider from '@components/Divider'
import PageTitle from '@components/PageTitle'
import QuestionPager from '@components/QuestionPager'
import ReviewAlerts from '@components/ReviewAlerts'
import SelectInput, { Option } from '@components/SelectInput'
import SourceQuestion from '@components/SourceQuestion'
import Template from '@components/Template'
import TextEditor from '@components/TextEditor'
import { errorMessages, successMessages } from '@constants/feedbackMessages'
import { yupResolver } from '@hookform/resolvers/yup'
import { addQuizSchema } from '@schemas/addQuiz.schema'
import { aiGenerationApi } from '@services/AiGenerationApi'
import { bancaApi } from '@services/BancaApi'
import { disciplineApi } from '@services/DisciplineApi'
import { filtersApi } from '@services/filtersApi'
import { questionGeneratorApi } from '@services/GenerateQuestions'
import { institutionApi } from '@services/InstitutionApi'
import { occupationApi } from '@services/OccupationApi'
import { yearApi } from '@services/YearApi'
import { openInputModal } from '@store/ducks/modal'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { inputIsValid } from '@utils/helpers'
import {
	createBreadcrumbLink,
	useCreateBreadcrumb,
	useQuery,
} from '@utils/hooks'

type Inputs = {
	disciplineSelect: MultiValue<{ value: number; label: string }>
	subjectSelect: MultiValue<{ value: number; label: string }>
	subSubjectSelect: MultiValue<{ value: number; label: string }>
	yearSelect?: MultiValue<{ value: number; label: string }>
	occupationSelect?: MultiValue<{ value: number; label: string }>
	bancaSelect?: MultiValue<{ value: number; label: string }>
	institutionSelect?: MultiValue<{ value: number; label: string }>
	title: string
	enunciado: string
	comment: string
	trueOrFalse: number
}

const styles = {
	searchContainer: 'gap-[8px] mb-[32px] items-end flex-wrap',
	divider: 'mt-[32px] mb-[12px]',
}

function EditQuestionGenerator() {
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const [getQuiz, { data: quiz }] = disciplineApi.useShowQuizMutationMutation()
	const { inputValue } = useAppSelector((state) => state.modal)
	const inputValueRef = useRef(inputValue)
	const [lastAction, setLastAction] = useState<'approve' | 'reject' | null>(
		null
	)

	useEffect(() => {
		inputValueRef.current = inputValue
	}, [inputValue])
	const { id } = useParams<{
		id: string
	}>()

	const [subject, setSubject] = useState<
		Array<{ option: string; value: number }>
	>([])

	const [discipline, setDiscipline] = useState<
		Array<{ option: string; value: number }>
	>([])

	const [subSubject, setSubSubject] = useState<
		Array<{ option: string; value: number }>
	>([])

	const [year, setYear] = useState<Array<{ option: string; value: number }>>([])

	const [occupation, setOccupation] = useState<
		Array<{ option: string; value: number }>
	>([])

	const [banca, setBanca] = useState<Array<{ option: string; value: number }>>(
		[]
	)

	const [institution, setInstitution] = useState<
		Array<{ option: string; value: number }>
	>([])

	const [disciplineId, setDisciplineId] = useState<string>()
	const [subjectId, setSubjectId] = useState<string>()
	const [enunciado, setEnunciado] = useState(quiz?.description)
	const [comment, setComment] = useState(quiz?.explanation)

	const baseUrl = `/questoes/criar-questao`

	const allPages = useMemo(() => {
		return [createBreadcrumbLink(`Cadastro Questão`, baseUrl, 1)]
	}, [baseUrl])

	const pages = useCreateBreadcrumb(allPages)

	const {
		register,
		handleSubmit,
		reset,
		setValue,
		formState: { errors, touchedFields },
		control,
	} = useForm<Inputs>({
		mode: 'onBlur',
		resolver: yupResolver(addQuizSchema),
	})

	const [editQuiz, { isLoading: isSaving }] =
		disciplineApi.useEditQuizMutation()

	const modalOptions = {
		title: 'Motivo',
		inputPlaceholder: 'Digite aqui o motivo',
		buttonLabel: 'Confirmar',
	}

	const query = useQuery()

	const { data: dataQuestions } = questionGeneratorApi.useListQuery({
		page: 1,
		limit: 10000,
	})

	/*
	 * De qual conversão esta questão veio. A URL traz quando se chega pelo
	 * detalhe da conversão; nos outros caminhos — lista de revisão, link direto
	 * — a própria questão responde.
	 */
	const generationId =
		query.get('geracao') ||
		(quiz?.aiReview ? String(quiz.aiReview.generationId) : '')

	const { data: generationQuestions, refetch: refetchSiblings } =
		aiGenerationApi.useQuestionsQuery(
			{ id: generationId, page: 1, limit: 1000 },
			{ skip: !generationId }
		)

	/*
	 * As reprovadas saem da navegação: já foram decididas, e continuar
	 * passando por elas embaralha a contagem de quem ainda tem o caderno para
	 * revisar. Elas seguem na listagem da conversão, com o status à vista — lá
	 * o que interessa é a prestação de contas, aqui é o que falta decidir.
	 *
	 * A questão aberta fica na lista mesmo reprovada: é o que mantém o avançar
	 * na tela logo depois de reprovar, e para quem chegou nela por um link.
	 */
	const siblings =
		generationQuestions?.data
			.filter(
				(entry) =>
					entry.review?.decision !== 'rejected' ||
					entry.question?.id === Number(id)
			)
			.map((entry) => entry.question?.id)
			.filter((value): value is number => typeof value === 'number') ?? []

	const [
		approveRejectQuestion,
		{ isSuccess: isSuccessApproveReject, isLoading: isDeciding },
	] = questionGeneratorApi.useApproveRejectMutation()

	useEffect(() => {
		if (isSuccessApproveReject) {
			if (lastAction === 'approve') {
				dispatch(
					notificationCreators.show(
						successMessages.storedGenerateQuestionApproved
					)
				)
			} else if (lastAction === 'reject') {
				dispatch(
					notificationCreators.show(
						successMessages.storedGenerateQuestionReject
					)
				)
			}
			setLastAction(null)
		}
	}, [isSuccessApproveReject, lastAction, dispatch])

	const questionsIds = dataQuestions?.data.map((questions) => questions.id)

	const buildQuizPayload = (data: Inputs) => ({
		id: Number(id),
		title: data.title,
		description: data.enunciado,
		explanation: data.comment,
		isTrue: !!data.trueOrFalse,
		disciplineIds: data.disciplineSelect.map((v) => v.value as number),
		matterIds: data.subjectSelect.map((v) => v.value as number),
		submatterIds: data.subSubjectSelect.map((v) => v.value as number) || [],
		yearIds: data.yearSelect?.map((v) => v.value as number) || [],
		positionIds: data.occupationSelect?.map((v) => v.value as number) || [],
		juryIds: data.bancaSelect?.map((v) => v.value as number) || [],
		institutionIds: data.institutionSelect?.map((v) => v.value as number) || [],
	})

	const goToNextQuestion = () => {
		// A ordem do caderno, e não a da fila global de pendentes: quem revisa
		// está com o PDF de uma conversão aberto ao lado.
		const sequence = siblings.length > 0 ? siblings : questionsIds ?? []
		const position = sequence.indexOf(Number(id))
		const next = position < 0 ? undefined : sequence[position + 1]

		if (next === undefined) {
			return
		}

		navigate(
			generationId
				? `/revisar-questoes/${next}?geracao=${generationId}`
				: `/revisar-questoes/${next}`
		)
	}

	/*
	 * Aprovar salva o que está na tela antes de decidir — senão a edição de quem
	 * clica direto em Aprovar é descartada e a versão da IA vai para o app. Se o
	 * salvar falha, a aprovação não acontece.
	 */
	const approveQuestion = handleSubmit(
		async (data) => {
			try {
				await editQuiz(buildQuizPayload(data)).unwrap()
			} catch {
				dispatch(notificationCreators.show(errorMessages.saveQuestion))

				return
			}

			setLastAction('approve')

			try {
				await approveRejectQuestion({
					isApproved: true,
					questionIds: [Number(id)],
				}).unwrap()
			} catch {
				setLastAction(null)
				dispatch(notificationCreators.show(errorMessages.approveQuestion))

				return
			}

			refetchSiblings()
			goToNextQuestion()
		},
		() => dispatch(notificationCreators.show(errorMessages.incompleteQuestion))
	)

	/*
	 * Reprovar segue para a próxima, como o aprovar: a questão acabou de sair
	 * da navegação, e ficar parado nela obriga a procurar onde se estava.
	 */
	const rejectQuestion = async () => {
		setLastAction('reject')

		try {
			await approveRejectQuestion({
				isApproved: false,
				questionIds: [Number(id)],
				refuseReason: inputValueRef.current,
			}).unwrap()
		} catch {
			setLastAction(null)
			dispatch(notificationCreators.show(errorMessages.rejectQuestion))

			return
		}

		refetchSiblings()
		goToNextQuestion()
	}

	const onSubmit: SubmitHandler<Inputs> = async (data) => {
		try {
			await editQuiz(buildQuizPayload(data)).unwrap()
		} catch {
			dispatch(notificationCreators.show(errorMessages.saveQuestion))

			return
		}

		dispatch(notificationCreators.show(successMessages.updatedQuiz))

		// Quem veio revisar uma conversão fica na questão: ainda falta decidir se
		// ela é aprovada.
		if (!generationId) {
			navigate(`/questoes`)
		}
	}

	useEffect(() => {
		if (id) {
			getQuiz({ id: Number(id) as number })
		}
	}, [id, getQuiz])

	/*
	 * Avançar para outra questão muda a URL, mas casa com o mesmo padrão de
	 * rota: o React reaproveita este componente em vez de remontá-lo, e o
	 * formulário chega na questão nova com os erros e o "já tocado" da anterior
	 * — bordas que não falam do que está na tela. Os valores ficam: quem os
	 * repõe é o efeito que lê o quiz.
	 */
	useEffect(() => {
		reset(undefined, { keepValues: true })
	}, [id, reset])

	useEffect(() => {
		const newDiscipline: typeof discipline = []
		const newSubSubject: typeof subSubject = []
		const newSubject: typeof subject = []
		const newYear: typeof year = []
		const newOccupation: typeof occupation = []
		const newBanca: typeof banca = []
		const newIntitution: typeof institution = []

		if (quiz?.subMatters && quiz.subMatters.length > 0) {
			quiz.subMatters.forEach((e) => {
				newSubSubject.push({
					option: e.name,
					value: e.id as number,
				})

				newSubject.push({
					option: e.matter.name,
					value: e.matter.id as number,
				})

				newDiscipline.push({
					option: e.matter.discipline.name,
					value: e.matter.discipline.id as number,
				})
			})
		}

		if (quiz?.filters.yearFilters && quiz.filters.yearFilters.length > 0) {
			quiz.filters.yearFilters.forEach((e) => {
				newYear.push({
					option: e.name,
					value: e.id as number,
				})
			})
		}

		if (
			quiz?.filters.positionFilters &&
			quiz.filters.positionFilters.length > 0
		) {
			quiz.filters.positionFilters.forEach((e) => {
				newOccupation.push({
					option: e.name,
					value: e.id as number,
				})
			})
		}

		if (quiz?.filters.juryFilters && quiz.filters.juryFilters.length > 0) {
			quiz.filters.juryFilters.forEach((e) => {
				newBanca.push({
					option: e.name,
					value: e.id as number,
				})
			})
		}

		if (
			quiz?.filters.institutionFilters &&
			quiz.filters.institutionFilters.length > 0
		) {
			quiz.filters.institutionFilters.forEach((e) => {
				newIntitution.push({
					option: e.name,
					value: e.id as number,
				})
			})
		}
		setDiscipline(newDiscipline)
		setSubject(newSubject)
		setSubSubject(newSubSubject)
		setValue('trueOrFalse', quiz?.isTrue ? quiz?.isTrue : 0)
		setYear(newYear)
		setOccupation(newOccupation)
		setBanca(newBanca)
		setInstitution(newIntitution)
	}, [quiz, setValue])

	useEffect(() => {
		setValue(
			'disciplineSelect',
			discipline?.map((e) => ({ value: e.value, label: e.option }))
		)
		setValue(
			'subjectSelect',
			subject?.map((e) => ({ value: e.value, label: e.option }))
		)
		setValue(
			'subSubjectSelect',
			subSubject?.map((e) => ({ value: e.value, label: e.option }))
		)
		setValue(
			'yearSelect',
			year?.map((e) => ({ value: e.value, label: e.option }))
		)
		setValue(
			'occupationSelect',
			occupation?.map((e) => ({ value: e.value, label: e.option }))
		)
		setValue(
			'bancaSelect',
			banca?.map((e) => ({ value: e.value, label: e.option }))
		)
		setValue(
			'institutionSelect',
			institution?.map((e) => ({ value: e.value, label: e.option }))
		)
		if (quiz) {
			setValue('title', quiz.title)
			setEnunciado(quiz?.description)
			setComment(quiz?.explanation)
			setValue('enunciado', quiz.description)
			setValue('comment', quiz.explanation)
			setDisciplineId(discipline?.map((e) => e.value).join(','))
			setSubjectId(subject?.map((e) => e.value).join(','))
		}
	}, [
		banca,
		institution,
		occupation,
		quiz,
		setValue,
		subSubject,
		year,
		discipline,
		subject,
	])

	const { data: dataDiscipline } = disciplineApi.useListQuery({
		page: 1,
		search: '',
		limit: 100000,
	})

	const { data: dataSubject } = filtersApi.useListMatterQuery({
		disciplineId: disciplineId || '-1',
	})

	const { data: dataSubSubject } = filtersApi.useListSubMatterQuery({
		matterId: subjectId || '-1',
	})

	const { data: dataYear } = yearApi.useListQuery({
		page: 1,
		search: '',
		limit: 100000,
	})
	const { data: dataOccupation } = occupationApi.useListQuery({
		page: 1,
		search: '',
		limit: 100000,
	})

	const { data: dataBanca } = bancaApi.useListQuery({
		page: 1,
		search: '',
		limit: 100000,
	})

	const { data: dataInstitution } = institutionApi.useListQuery({
		page: 1,
		search: '',
		limit: 100000,
	})

	const typeOptions = useMemo(
		() => [
			{ option: 'VERDADEIRO', value: 1 },
			{ option: 'FALSO', value: 0 },
		],
		[]
	)
	const onTypeChange = useCallback(
		(v: Option) => setValue('trueOrFalse', Number(v.value)),
		[setValue]
	)

	return (
		<>
			<Template>
				<Breadcrumb pages={pages} />
				<PageTitle title='Edição de Questão' />

				{/*
				 * Antes dos campos, e não depois: quem abre esta tela vem para
				 * decidir se aprova, e a ressalva é o que decide. Embaixo do
				 * formulário ela seria lida depois da decisão já tomada.
				 */}
				{generationId && (
					<div className='mt-4 flex items-center justify-between flex-wrap gap-3'>
						<QuestionPager
							siblings={siblings}
							current={Number(id)}
							generationId={generationId}
						/>

						{/*
						 * O que já foi decidido, e por quem. Uma questão aprovada
						 * continua editável e reprovável — o que não pode é a tela
						 * ficar igual à de uma que ninguém olhou, porque aí a
						 * segunda pessoa refaz o trabalho da primeira.
						 */}
						{quiz?.aiReview?.decision && (
							<span className='text-sm text-gray'>
								{quiz.aiReview.decision === 'approved'
									? 'Aprovada'
									: 'Reprovada'}
								{quiz.aiReview.decidedBy?.name
									? ` por ${quiz.aiReview.decidedBy.name}`
									: ''}
								{quiz.aiReview.decidedAt
									? ` em ${new Date(quiz.aiReview.decidedAt).toLocaleString(
											'pt-BR',
											{
												dateStyle: 'short',
												timeStyle: 'short',
											}
									  )}`
									: ''}
							</span>
						)}
					</div>
				)}

				{quiz?.aiReview && (
					<div className='mt-6 flex flex-col gap-4'>
						<ReviewAlerts
							review={quiz.aiReview}
							variant='panel'
							title={quiz.title}
							explanation={quiz.explanation}
							subMatterCount={quiz.subMatters?.length ?? 0}
						/>

						<SourceQuestion review={quiz.aiReview} />
					</div>
				)}

				<form onSubmit={handleSubmit(onSubmit)} className='mt-6'>
					<div className='flex gap-3 mb-5 justify-end'>
						<Button
							onClick={approveQuestion}
							isLoading={isSaving || isDeciding}
							disabled={isSaving || isDeciding}
						>
							{quiz?.aiReview?.decision === 'approved'
								? 'Aprovar de novo'
								: 'Aprovar'}
						</Button>

						<Button
							variant='danger'
							isLoading={isDeciding}
							disabled={isSaving || isDeciding}
							onClick={() =>
								dispatch(
									openInputModal({
										...modalOptions,
										onClick: rejectQuestion,
									})
								)
							}
						>
							Reprovar
						</Button>
					</div>
					<div className='grid grid-cols-2 gap-4'>
						<InputGroup label='Disciplina' id='disciplineSelect'>
							<Controller
								control={control}
								name='disciplineSelect'
								render={({
									field: { value, onBlur },
									fieldState: { isTouched, error },
								}) => {
									return (
										<Select
											onChange={(v) => {
												setDisciplineId(v?.map((val) => val.value).join(','))
												setValue('disciplineSelect', v)
											}}
											value={value}
											options={
												dataDiscipline?.data
													? dataDiscipline.data.map((item) => ({
															value: item.id,
															label: item.name,
													  }))
													: []
											}
											defaultValue={
												discipline.length > 0
													? discipline.map(
															({ option, value: disciplineSelectId }) => ({
																value: disciplineSelectId as number,
																label: option,
															})
													  )
													: undefined
											}
											isMulti
											onBlur={onBlur}
											styles={{
												input: (base) => ({
													...base,
													borderRadius: '6px',
													paddingLeft: '2.5px',
													paddingRight: '2.5px',
													paddingTop: '3.5px',
													paddingBottom: '3.5px',
												}),
												control: (controlStyles) => ({
													...controlStyles,
													borderColor: (() => {
														const isValid = inputIsValid(error, isTouched)

														if (typeof isValid === 'undefined') {
															return '#D1D5DB !important'
														}

														if (!isValid) {
															return '#F44545 !important'
														}

														return '#15CA71 !important'
													})(),
												}),
												option: (optionStyle, { isFocused }) => ({
													...optionStyle,
													backgroundColor: isFocused ? '#1673FA' : '#fff',
													color: isFocused ? '#fff' : '#000',
												}),
											}}
											placeholder=''
											classNamePrefix='react-select'
										/>
									)
								}}
							/>
						</InputGroup>
						<InputGroup label='Assunto' id='subjectSelect'>
							<Controller
								control={control}
								name='subjectSelect'
								render={({
									field: { value, onBlur },
									fieldState: { isTouched, error },
								}) => {
									return (
										<Select
											onChange={(v) => {
												setSubjectId(v?.map((val) => val.value).join(','))
												setValue('subjectSelect', v)
											}}
											value={value}
											options={
												dataSubject?.data
													? dataSubject.data.map((item) => ({
															value: item.id,
															label: item.name,
													  }))
													: []
											}
											defaultValue={
												subject.length > 0
													? subject.map(({ option, value: matterId }) => ({
															value: matterId as number,
															label: option,
													  }))
													: undefined
											}
											isMulti
											onBlur={onBlur}
											styles={{
												input: (base) => ({
													...base,
													borderRadius: '6px',
													paddingLeft: '2.5px',
													paddingRight: '2.5px',
													paddingTop: '3.5px',
													paddingBottom: '3.5px',
												}),
												control: (controlStyles) => ({
													...controlStyles,
													borderColor: (() => {
														const isValid = inputIsValid(error, isTouched)

														if (typeof isValid === 'undefined') {
															return '#D1D5DB !important'
														}

														if (!isValid) {
															return '#F44545 !important'
														}

														return '#15CA71 !important'
													})(),
												}),
												option: (optionStyle, { isFocused }) => ({
													...optionStyle,
													backgroundColor: isFocused ? '#1673FA' : '#fff',
													color: isFocused ? '#fff' : '#000',
												}),
											}}
											placeholder=''
											classNamePrefix='react-select'
										/>
									)
								}}
							/>
						</InputGroup>
					</div>
					<div className='grid grid-cols-2 gap-4'>
						<InputGroup label='Sub Assunto' id='subSubjectSelect'>
							<Controller
								control={control}
								name='subSubjectSelect'
								render={({
									field: { value, onBlur },
									fieldState: { isTouched, error },
								}) => {
									return (
										<Select
											onChange={(v) => setValue('subSubjectSelect', v)}
											options={
												dataSubSubject?.data
													? dataSubSubject.data.map((item) => ({
															value: item.id,
															label: item.name,
													  }))
													: []
											}
											value={value}
											defaultValue={
												subSubject.length > 0
													? subSubject.map(
															({ option, value: submatterId }) => ({
																value: submatterId as number,
																label: option,
															})
													  )
													: undefined
											}
											isMulti
											onBlur={onBlur}
											styles={{
												input: (base) => ({
													...base,
													borderRadius: '6px',
													paddingLeft: '2.5px',
													paddingRight: '2.5px',
													paddingTop: '3.5px',
													paddingBottom: '3.5px',
												}),
												control: (controlStyles) => ({
													...controlStyles,
													borderColor: (() => {
														const isValid = inputIsValid(error, isTouched)

														if (typeof isValid === 'undefined') {
															return '#D1D5DB !important'
														}

														if (!isValid) {
															return '#F44545 !important'
														}

														return '#15CA71 !important'
													})(),
												}),
												option: (optionStyle, { isFocused }) => ({
													...optionStyle,
													backgroundColor: isFocused ? '#1673FA' : '#fff',
													color: isFocused ? '#fff' : '#000',
												}),
											}}
											placeholder=''
											classNamePrefix='react-select'
										/>
									)
								}}
							/>
						</InputGroup>
						<InputGroup label='Ano' id='yearSelect'>
							<Controller
								control={control}
								name='yearSelect'
								render={({
									field: { value, onBlur },
									fieldState: { isTouched, error },
								}) => {
									return (
										<Select
											onChange={(v) => setValue('yearSelect', v)}
											options={
												dataYear?.data
													? dataYear.data.map((item) => ({
															value: item.id,
															label: item.name,
													  }))
													: []
											}
											value={value}
											isMulti
											onBlur={onBlur}
											styles={{
												input: (base) => ({
													...base,
													borderRadius: '6px',
													paddingLeft: '2.5px',
													paddingRight: '2.5px',
													paddingTop: '3.5px',
													paddingBottom: '3.5px',
												}),
												control: (controlStyles) => ({
													...controlStyles,
													borderColor: (() => {
														const isValid = inputIsValid(error, isTouched)

														if (typeof isValid === 'undefined') {
															return '#D1D5DB !important'
														}

														if (!isValid) {
															return '#F44545 !important'
														}

														return '#15CA71 !important'
													})(),
												}),
												option: (optionStyle, { isFocused }) => ({
													...optionStyle,
													backgroundColor: isFocused ? '#1673FA' : '#fff',
													color: isFocused ? '#fff' : '#000',
												}),
											}}
											placeholder=''
											classNamePrefix='react-select'
										/>
									)
								}}
							/>
						</InputGroup>
						<InputGroup label='Cargo' id='occupationSelect'>
							<Controller
								control={control}
								name='occupationSelect'
								render={({
									field: { value, onBlur },
									fieldState: { isTouched, error },
								}) => {
									return (
										<Select
											onChange={(v) => setValue('occupationSelect', v)}
											options={
												dataOccupation?.data
													? dataOccupation.data.map((item) => ({
															value: item.id,
															label: item.name,
													  }))
													: []
											}
											value={value}
											isMulti
											onBlur={onBlur}
											styles={{
												input: (base) => ({
													...base,
													borderRadius: '6px',
													paddingLeft: '2.5px',
													paddingRight: '2.5px',
													paddingTop: '3.5px',
													paddingBottom: '3.5px',
												}),
												control: (controlStyles) => ({
													...controlStyles,
													borderColor: (() => {
														const isValid = inputIsValid(error, isTouched)

														if (typeof isValid === 'undefined') {
															return '#D1D5DB !important'
														}

														if (!isValid) {
															return '#F44545 !important'
														}

														return '#15CA71 !important'
													})(),
												}),
												option: (optionStyle, { isFocused }) => ({
													...optionStyle,
													backgroundColor: isFocused ? '#1673FA' : '#fff',
													color: isFocused ? '#fff' : '#000',
												}),
											}}
											placeholder=''
											classNamePrefix='react-select'
										/>
									)
								}}
							/>
						</InputGroup>
						<InputGroup label='Banca' id='bancaSelect'>
							<Controller
								control={control}
								name='bancaSelect'
								render={({
									field: { value, onBlur },
									fieldState: { isTouched, error },
								}) => {
									return (
										<Select
											onChange={(v) => setValue('bancaSelect', v)}
											options={
												dataBanca?.data
													? dataBanca.data.map((item) => ({
															value: item.id,
															label: item.name,
													  }))
													: []
											}
											value={value}
											isMulti
											onBlur={onBlur}
											styles={{
												input: (base) => ({
													...base,
													borderRadius: '6px',
													paddingLeft: '2.5px',
													paddingRight: '2.5px',
													paddingTop: '3.5px',
													paddingBottom: '3.5px',
												}),
												control: (controlStyles) => ({
													...controlStyles,
													borderColor: (() => {
														const isValid = inputIsValid(error, isTouched)

														if (typeof isValid === 'undefined') {
															return '#D1D5DB !important'
														}

														if (!isValid) {
															return '#F44545 !important'
														}

														return '#15CA71 !important'
													})(),
												}),
												option: (optionStyle, { isFocused }) => ({
													...optionStyle,
													backgroundColor: isFocused ? '#1673FA' : '#fff',
													color: isFocused ? '#fff' : '#000',
												}),
											}}
											placeholder=''
											classNamePrefix='react-select'
										/>
									)
								}}
							/>
						</InputGroup>
						<InputGroup label='Instituição' id='institutionSelect'>
							<Controller
								control={control}
								name='institutionSelect'
								render={({
									field: { value, onBlur },
									fieldState: { isTouched, error },
								}) => {
									return (
										<Select
											onChange={(v) => setValue('institutionSelect', v)}
											options={
												dataInstitution?.data
													? dataInstitution.data.map((item) => ({
															value: item.id,
															label: item.name,
													  }))
													: []
											}
											value={value}
											isMulti
											onBlur={onBlur}
											styles={{
												input: (base) => ({
													...base,
													borderRadius: '6px',
													paddingLeft: '2.5px',
													paddingRight: '2.5px',
													paddingTop: '3.5px',
													paddingBottom: '3.5px',
												}),
												control: (controlStyles) => ({
													...controlStyles,
													borderColor: (() => {
														const isValid = inputIsValid(error, isTouched)

														if (typeof isValid === 'undefined') {
															return '#D1D5DB !important'
														}

														if (!isValid) {
															return '#F44545 !important'
														}

														return '#15CA71 !important'
													})(),
												}),
												option: (optionStyle, { isFocused }) => ({
													...optionStyle,
													backgroundColor: isFocused ? '#1673FA' : '#fff',
													color: isFocused ? '#fff' : '#000',
												}),
											}}
											placeholder=''
											classNamePrefix='react-select'
										/>
									)
								}}
							/>
						</InputGroup>
						<InputGroup label='Título' id='title'>
							<Input
								type='text'
								{...register('title')}
								isValid={inputIsValid(errors.title, touchedFields.title)}
							/>
						</InputGroup>
					</div>

					<Controller
						{...register('enunciado')}
						name='enunciado'
						control={control}
						render={({ field: { onBlur } }) => (
							<InputGroup
								className={`mt-6 index-0-controler ${
									errors.enunciado && 'error-textBox'
								}`}
								label='Enunciado'
								id='enunciado'
							>
								<TextEditor
									onChange={(e) => {
										setEnunciado(e)
										setValue('enunciado', e)
									}}
									value={enunciado}
									onBlur={onBlur}
								/>
							</InputGroup>
						)}
					/>

					<Controller
						{...register('comment')}
						name='comment'
						control={control}
						render={({ field: { onBlur } }) => (
							<InputGroup
								className={`mt-6 ${errors.comment && 'error-textBox'}`}
								label='Comentário'
								id='comment'
							>
								<TextEditor
									onChange={(e) => {
										setComment(e)
										setValue('comment', e)
									}}
									value={comment}
									onBlur={onBlur}
								/>
							</InputGroup>
						)}
					/>

					<div className='flex justify-start max-w-[50%]'>
						<InputGroup
							className='mt-6'
							label='Verdadeiro/Falso'
							id='trueOrFalse'
						>
							<SelectInput
								btnClass='px-[13px] py-[8px] w-[150px]'
								options={typeOptions}
								selectedValue={quiz?.isTrue}
								onChange={onTypeChange}
							/>
						</InputGroup>
					</div>

					<div className='flex gap-4 mt-6 mb-10'>
						<Button variant='outline' onClick={() => navigate(`/questoes`)}>
							Cancelar
						</Button>
						<Button type='submit'>Editar Questão</Button>
					</div>
				</form>
				<Divider className={styles.divider} />
			</Template>
		</>
	)
}

export default EditQuestionGenerator
